import React from 'react'
import { Stack, Typography } from '@mui/material'
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  CalculateOutlined as CalculateIcon,
  MonitorWeightOutlined as MonitorWeightOutlinedIcon,
  Backspace as BackspaceIcon,
} from '@mui/icons-material'
import { Chip } from 'components/generic/chip'

export const INPUT_FOCUS_INDEX = {
  NAME: 0,
  GROSS: 1,
  TARE: 2,
  PRICE: 3,
}

export const generateAxleFocusIndex = numberOfAxles => {
  const AXLE_FOCUS_INDEX = {}
  for (let i = 1; i <= numberOfAxles; i++) {
    AXLE_FOCUS_INDEX[`AXLE-${i}-GROSS`] = i
    AXLE_FOCUS_INDEX[`AXLE-${i}-TARE`] = i + numberOfAxles
  }
  return AXLE_FOCUS_INDEX
}

export const onKeyPressNumberField = (event, options = {}) => {
  const { showWeightDecimals = false } = options
  const { value, name } = event.target
  const isPriceField = name?.indexOf('price') > -1
  const regExp = (showWeightDecimals || isPriceField) && value?.indexOf('.') === -1 ? /[0-9.]+/g : /[0-9]+/g
  if (!regExp.test(event.key)) {
    event.preventDefault()
  }
}

export const formatAndSavePrice = (value, setValue) => {
  switch (value) {
    case '-':
      setValue('material.price', '-')
      break
    case '.':
      setValue('material.price', '0.')
      break
    case '-.':
      setValue('material.price', '-0.')
      break
    default:
      if (Number.isNaN(Number(value))) return
      setValue('material.price', value)
      break
  }
}

export const buttonDisabled = (button, inputFocusIndex, config = {}) => {
  const {
    startIndex = INPUT_FOCUS_INDEX.NAME,
    endIndex = INPUT_FOCUS_INDEX.PRICE,
    isScrapVehicle = false,
    priceIsValid = true,
    showWeightDecimals = false,
    updateWeightPermission = true,
  } = config

  if (button.id === 'buttonDone') {
    return !priceIsValid
  }

  if (isScrapVehicle) {
    return true
  }

  if (button.id === 'buttonAxleWeights') {
    return false
  }

  if (button.id === 'buttonUp' && inputFocusIndex === startIndex) {
    return true
  }

  if (button.id === 'buttonDown' && inputFocusIndex === endIndex) {
    return true
  }

  if (
    !showWeightDecimals &&
    button.id === 'buttonDot' &&
    (inputFocusIndex === INPUT_FOCUS_INDEX.GROSS || inputFocusIndex === INPUT_FOCUS_INDEX.TARE)
  ) {
    return true
  }

  if (
    button.id === 'buttonHyphen' &&
    (inputFocusIndex === INPUT_FOCUS_INDEX.GROSS || inputFocusIndex === INPUT_FOCUS_INDEX.TARE)
  ) {
    return true
  }

  if (
    button.id === 'buttonWeight' &&
    (inputFocusIndex === INPUT_FOCUS_INDEX.NAME || inputFocusIndex === INPUT_FOCUS_INDEX.PRICE)
  ) {
    return true
  }

  if (inputFocusIndex === INPUT_FOCUS_INDEX.NAME) {
    return button.id !== 'buttonDown'
  }

  if (!updateWeightPermission && startIndex === INPUT_FOCUS_INDEX.GROSS && endIndex === INPUT_FOCUS_INDEX.TARE) {
    return true
  }

  if (!updateWeightPermission && inputFocusIndex !== INPUT_FOCUS_INDEX.PRICE) {
    return button.id !== 'buttonDown' && button.id !== 'buttonUp'
  }

  return false
}

export const getInputButtons = (defaultYardUm, enableAxleWeights, hasAxleWeights) => [
  {
    id: 'button7',
    content: <Typography variant="h2">7</Typography>,
    value: '7',
  },
  {
    id: 'button8',
    content: <Typography variant="h2">8</Typography>,
    value: '8',
  },
  {
    id: 'button9',
    content: <Typography variant="h2">9</Typography>,
    value: '9',
  },
  {
    id: 'buttonUp',
    content: <KeyboardArrowUpIcon fontSize="large" />,
    value: '',
  },
  {
    id: 'button4',
    content: <Typography variant="h2">4</Typography>,
    value: '4',
  },
  {
    id: 'button5',
    content: <Typography variant="h2">5</Typography>,
    value: '5',
  },
  {
    id: 'button6',
    content: <Typography variant="h2">6</Typography>,
    value: '6',
  },
  {
    id: 'buttonDown',
    content: <KeyboardArrowDownIcon fontSize="large" />,
    value: '',
  },
  {
    id: 'button1',
    content: <Typography variant="h2">1</Typography>,
    value: '1',
  },
  {
    id: 'button2',
    content: <Typography variant="h2">2</Typography>,
    value: '2',
  },
  {
    id: 'button3',
    content: <Typography variant="h2">3</Typography>,
    value: '3',
  },
  {
    id: enableAxleWeights ? 'buttonAxleWeights' : 'buttonWeight',
    content: enableAxleWeights ? (
      <Stack spacing={1} alignItems="center">
        <CalculateIcon fontSize="large" />
        {hasAxleWeights && (
          <Chip
            label="Enabled"
            variant="filled"
            size="small"
            color="info"
            style={{ textTransform: 'none', cursor: 'pointer' }}
          />
        )}
      </Stack>
    ) : (
      <Stack spacing={1}>
        <MonitorWeightOutlinedIcon fontSize="large" />
        <Typography sx={{ fontSize: 14 }}>{`0 ${defaultYardUm}`}</Typography>
      </Stack>
    ),
    value: '0',
  },
  {
    id: 'buttonBack',
    content: <BackspaceIcon fontSize="large" />,
    value: '',
  },
  {
    id: 'button0',
    content: <Typography variant="h2">0</Typography>,
    value: '0',
  },
  {
    id: 'buttonDot',
    content: <Typography variant="h2">.</Typography>,
    value: '.',
  },
  {
    id: 'buttonHyphen',
    content: <Typography variant="h2">-</Typography>,
    value: '-',
  },
  {
    id: 'buttonDone',
    content: (
      <Typography color="white" variant="h5">
        Done
      </Typography>
    ),
    value: '',
  },
]

const handleButtonShift = ({
  fieldList,
  fieldListIndex,
  setInputFocusIndex,
  updateWeightPermission,
  isDown = false,
  numberOfAxles,
}) => {
  const FOCUS_INDEX = numberOfAxles ? generateAxleFocusIndex(numberOfAxles) : INPUT_FOCUS_INDEX
  const direction = isDown ? 1 : -1
  if (isDown ? fieldListIndex < fieldList.length - 1 : fieldListIndex > 0) {
    let newFieldListIndex = fieldListIndex + direction * 1

    // skipping focus on Gross and Tare inputs when user does is not auth to update them
    if (!updateWeightPermission) {
      if (newFieldListIndex === FOCUS_INDEX[isDown ? 'GROSS' : 'TARE']) {
        newFieldListIndex = fieldListIndex + direction * 2
      } else if (newFieldListIndex === FOCUS_INDEX[isDown ? 'TARE' : 'GROSS']) {
        newFieldListIndex = fieldListIndex + direction * 3
      }
    }
    fieldList[newFieldListIndex].ref.current.focus()
    const newInputFocusIndex = fieldList[newFieldListIndex].index
    setInputFocusIndex(newInputFocusIndex)
  }
}

export const onPressTab = ({
  event,
  fieldList,
  inputFocusIndex,
  setInputFocusIndex,
  isAdminOrScaleOp,
  numberOfAxles,
}) => {
  if (event.key === 'Tab') {
    event.preventDefault()
    if (event.shiftKey) {
      handleButtonShift({
        fieldList,
        fieldListIndex: fieldList.findIndex(field => field.index === inputFocusIndex),
        setInputFocusIndex,
        updateWeightPermission: isAdminOrScaleOp,
        numberOfAxles,
      })
    } else {
      handleButtonShift({
        fieldList,
        fieldListIndex: fieldList.findIndex(field => field.index === inputFocusIndex),
        setInputFocusIndex,
        updateWeightPermission: isAdminOrScaleOp,
        isDown: true,
        numberOfAxles,
      })
    }
  }
}

export const onClickEntryButton = (
  button,
  inputFocusIndex,
  setInputFocusIndex,
  fieldList,
  onDone,
  setValue,
  updateWeightPermission,
  onClickAxleWeightsButton,
  removeAxleWeights,
) => {
  if (fieldList.some(field => !field.ref)) return

  const fieldListIndex = fieldList.findIndex(field => field.index === inputFocusIndex)

  if (button.id === 'buttonDone') {
    onDone()
  } else if (button.id === 'buttonUp') {
    handleButtonShift({
      fieldList,
      fieldListIndex,
      setInputFocusIndex,
      updateWeightPermission,
    })
  } else if (button.id === 'buttonDown') {
    handleButtonShift({
      fieldList,
      fieldListIndex,
      setInputFocusIndex,
      updateWeightPermission,
      isDown: true,
    })
  } else if (button.id === 'buttonAxleWeights') {
    onClickAxleWeightsButton()
  } else {
    removeAxleWeights()
    const fieldListEntity = fieldList[fieldListIndex]
    const { ref, fieldName } = fieldListEntity
    ref.current.focus()
    const cursorStartIndex = ref.current.selectionStart
    const cursorEndIndex = ref.current.selectionEnd

    let currentValue = (ref.current.value?.toString() || '').split('')
    if (cursorStartIndex !== cursorEndIndex) {
      currentValue.splice(cursorStartIndex, cursorEndIndex - cursorStartIndex)
    }

    if (button.id === 'buttonBack') {
      if (cursorStartIndex === cursorEndIndex && cursorStartIndex > 0) {
        currentValue.splice(cursorStartIndex - 1, 1)
      }
    } else if (button.id === 'buttonWeight') {
      currentValue = [button.value]
    } else if (button.id === 'buttonDot') {
      if (currentValue.indexOf('.') === -1) {
        currentValue.splice(cursorStartIndex, 0, '.')
      }
    } else if (button.id === 'buttonHyphen') {
      if (currentValue.indexOf('-') === -1) {
        currentValue.splice(0, 0, '-')
      } else {
        currentValue.splice(0, 1)
      }
    } else {
      currentValue.splice(cursorStartIndex, 0, button.value)
    }

    if (currentValue[0] === '.') {
      currentValue.unshift('0')
    }
    if (currentValue[0] === '-' && currentValue[1] === '.') {
      currentValue.splice(1, 0, '0')
    }
    setValue(fieldName, currentValue.join(''))
  }
}
